import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Award, FolderOpen, Briefcase, FileCheck, History, ShieldCheck } from 'lucide-react';
import { adminApi, API_URL } from '../../services/api';
import { Avatar } from '../../components/ui/Avatar';
import { Skeleton } from '../../components/ui/Skeleton';
import { SecureImage } from '../../components/ui/SecureImage';
import { formatDate, formatDateTime } from '../../utils/helpers';
import type { User, ActivityLogEntry } from '../../types';

const userTypeLabels: Record<string, string> = { client: 'Cliente', worker: 'Trabajador', admin: 'Admin' };
const statusOptions = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'suspended', label: 'Suspendido' },
];
const verificationMeta: Record<string, { label: string; className: string }> = {
  pending: { label: 'Verificación pendiente', className: 'bg-warning-100 text-warning-700' },
  approved: { label: 'Verificado', className: 'bg-success-100 text-success-700' },
  rejected: { label: 'Verificación rechazada', className: 'bg-danger-100 text-danger-700' },
};
const profileStatusMeta: Record<string, { label: string; className: string }> = {
  draft: { label: 'Borrador', className: 'bg-secondary-100 text-secondary-600' },
  pending_review: { label: 'En revisión', className: 'bg-warning-100 text-warning-700' },
  needs_correction: { label: 'Requiere correcciones', className: 'bg-danger-100 text-danger-700' },
  verified: { label: 'Perfil verificado', className: 'bg-success-100 text-success-700' },
  rejected: { label: 'Perfil rechazado', className: 'bg-danger-100 text-danger-700' },
};
const statLabels: Record<string, string> = {
  completed_jobs: 'Completados',
  total_proposals: 'Propuestas',
  total_reviews: 'Reviews',
  rating_average: 'Rating',
  published_jobs: 'Publicados',
};
const activityLabels: Record<string, string> = {
  status_updated: 'Cambio de estado',
  verification_approved: 'Profesional aprobado',
  verification_rejected: 'Profesional rechazado',
  correction_requested: 'Correcciones solicitadas',
};

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
    <div className="px-6 py-4 border-b border-outlineVarWarm flex items-center gap-2">
      <span className="text-primary-600">{icon}</span>
      <h2 className="font-semibold text-onSurface">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

export const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<Record<string, any> | null>(null);
  const [activity, setActivity] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    adminApi
      .userDetail(Number(id))
      .then((res) => {
        setUser(res.data.user);
        setStats(res.data.stats);
        setActivity(res.data.activity || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStatusChange = async (status: string) => {
    if (!user) return;
    setActing(true);
    try {
      await adminApi.updateUserStatus(user.id, status);
      load();
    } finally {
      setActing(false);
    }
  };

  const handleVerify = async (status: 'approved' | 'rejected') => {
    if (!user) return;
    let rejection_reason: string | undefined;
    if (status === 'rejected') {
      rejection_reason = window.prompt('Motivo del rechazo (opcional):') || undefined;
    }
    setActing(true);
    try {
      await adminApi.updateUserVerification(user.id, { verification_status: status, rejection_reason });
      load();
    } finally {
      setActing(false);
    }
  };

  const handleRequestCorrection = async () => {
    if (!user) return;
    const notes = window.prompt('¿Qué tiene que corregir el profesional?');
    if (!notes) return;
    setActing(true);
    try {
      await adminApi.requestCorrection(user.id, notes);
      load();
    } finally {
      setActing(false);
    }
  };

  const handleToggleContact = async (field: 'phone' | 'email') => {
    if (!user) return;
    setActing(true);
    try {
      await adminApi.toggleContactVerification(user.id, field);
      load();
    } finally {
      setActing(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const isWorker = user.user_type === 'worker';
  const profile = user.worker_profile;
  const isPendingWorker = isWorker && user.verification_status === 'pending';
  const publicUrl = (path: string) => `${API_URL.replace(/\/api\/?$/, '')}/storage/${path}`;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-sm text-onSurface-var hover:text-onSurface">
        <ArrowLeft className="w-4 h-4" /> Volver a usuarios
      </Link>

      {/* Header */}
      <div className="bg-surface rounded-2xl border border-outlineVarWarm p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <Avatar src={user.avatar} name={user.full_name} size="xl" />
          <div>
            <h1 className="text-xl font-bold text-onSurface">{user.full_name}</h1>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span className="badge bg-primary-100 text-primary-700">{userTypeLabels[user.user_type]}</span>
              {isWorker && user.verification_status && (
                <span className={`badge ${verificationMeta[user.verification_status].className}`}>
                  {verificationMeta[user.verification_status].label}
                </span>
              )}
              {isWorker && profile && (
                <span className={`badge ${profileStatusMeta[profile.profile_status].className}`}>
                  {profileStatusMeta[profile.profile_status].label}
                </span>
              )}
            </div>
          </div>
        </div>
        <select
          value={user.status}
          disabled={acting}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="text-sm px-3 py-2 rounded-xl border border-outlineVarWarm bg-surface text-onSurface disabled:opacity-50"
        >
          {statusOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {user.rejection_reason && (
        <div className="p-4 rounded-xl bg-danger-50 text-danger-700 text-sm">
          <strong>Motivo de rechazo:</strong> {user.rejection_reason}
        </div>
      )}
      {isWorker && profile?.moderation_notes && profile.profile_status === 'needs_correction' && (
        <div className="p-4 rounded-xl bg-warning-50 text-warning-800 text-sm">
          <strong>Correcciones solicitadas:</strong> {profile.moderation_notes}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Datos privados */}
          <Section title="Datos de contacto (privado)" icon={<Mail className="w-5 h-5" />}>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-onSurface-mute" />
                <span className="text-onSurface">{user.email}</span>
                {isWorker && (
                  <button
                    onClick={() => handleToggleContact('email')}
                    disabled={acting}
                    className={`badge ${user.email ? 'bg-success-100 text-success-700' : 'bg-secondary-100 text-secondary-600'} disabled:opacity-50`}
                  >
                    email
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-onSurface-mute" />
                <span className="text-onSurface">{user.phone || '—'}</span>
                {isWorker && (
                  <button
                    onClick={() => handleToggleContact('phone')}
                    disabled={acting}
                    className={`badge ${profile?.phone_verified_at ? 'bg-success-100 text-success-700' : 'bg-secondary-100 text-secondary-600'} disabled:opacity-50`}
                  >
                    {profile?.phone_verified_at ? 'teléfono verificado' : 'marcar verificado'}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-onSurface-mute" />
                <span className="text-onSurface">{[user.city, user.state].filter(Boolean).join(', ') || '—'}</span>
              </div>
              {isWorker && profile?.dni && (
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-onSurface-mute" />
                  <span className="text-onSurface">DNI {profile.dni}</span>
                </div>
              )}
            </div>
            {user.bio && <p className="text-sm text-onSurface-var mt-4 whitespace-pre-wrap">{user.bio}</p>}
          </Section>

          {/* Identidad */}
          {isWorker && (
            <Section title="Verificación de identidad" icon={<ShieldCheck className="w-5 h-5" />}>
              <div className="grid sm:grid-cols-3 gap-4">
                {(['dni_front', 'dni_back', 'selfie'] as const).map((type) => (
                  <div key={type}>
                    <p className="text-xs text-onSurface-mute mb-1.5">
                      {type === 'dni_front' ? 'Frente del DNI' : type === 'dni_back' ? 'Dorso del DNI' : 'Selfie'}
                    </p>
                    {profile?.[`${type}_path` as keyof typeof profile] ? (
                      <SecureImage
                        path={adminApi.documentPath(user.id, type)}
                        alt={type}
                        className="w-full h-32 object-cover rounded-lg border border-outlineVarWarm"
                      />
                    ) : (
                      <div className="w-full h-32 rounded-lg border border-dashed border-outlineVarWarm flex items-center justify-center text-onSurface-mute text-xs">
                        Sin cargar
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Perfil público */}
          {isWorker && profile && (
            <Section title="Perfil público" icon={<Briefcase className="w-5 h-5" />}>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Field label="Título profesional" value={profile.professional_title} />
                <Field label="Años de experiencia" value={profile.years_experience?.toString()} />
                <Field label="Zona de cobertura" value={profile.coverage_radius_km ? `${profile.coverage_radius_km} km` : undefined} />
                <Field label="Disponibilidad" value={(profile.available_days || []).join(', ')} />
                <Field label="Horario" value={profile.available_hours} />
              </div>
              {user.categories && user.categories.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-onSurface-mute mb-1.5">Oficios</p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.categories.map((c) => (
                      <span key={c.id} className="badge bg-primary-100 text-primary-700">
                        {c.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {user.skills && user.skills.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-onSurface-mute mb-1.5 flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" /> Especialidades
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.skills.map((s) => (
                      <span key={s.id} className="badge bg-surface-var text-onSurface-var">
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Portfolio */}
          {isWorker && user.portfolios && user.portfolios.length > 0 && (
            <Section title="Trabajos realizados (portfolio)" icon={<FolderOpen className="w-5 h-5" />}>
              <div className="space-y-4">
                {user.portfolios.map((p) => (
                  <div key={p.id} className="border border-outlineVarWarm rounded-xl p-4">
                    <p className="font-medium text-onSurface">{p.title}</p>
                    {p.description && <p className="text-sm text-onSurface-var mt-1">{p.description}</p>}
                    <div className="flex gap-2 flex-wrap mt-3">
                      {(p.images || []).map((img, i) => (
                        <img
                          key={i}
                          src={publicUrl(img)}
                          alt={p.title}
                          className="w-20 h-20 object-cover rounded-lg border border-outlineVarWarm"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Matrícula */}
          {isWorker && (profile?.license_number || profile?.license_document_path) && (
            <Section title="Matrícula o habilitación" icon={<FileCheck className="w-5 h-5" />}>
              <div className="grid sm:grid-cols-2 gap-4 text-sm mb-4">
                <Field label="Tipo" value={profile.license_type} />
                <Field label="Número" value={profile.license_number} />
                <Field label="Organismo emisor" value={profile.license_issuer} />
                <Field label="Vencimiento" value={profile.license_expires_at ? formatDate(profile.license_expires_at) : undefined} />
              </div>
              {profile.license_document_path && (
                <SecureImage
                  path={adminApi.documentPath(user.id, 'license_document')}
                  alt="Documento de matrícula"
                  className="w-40 h-32 object-cover rounded-lg border border-outlineVarWarm"
                />
              )}
            </Section>
          )}

          {/* Referencia */}
          {isWorker && profile?.reference_name && (
            <Section title="Referencia profesional" icon={<Phone className="w-5 h-5" />}>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <Field label="Nombre" value={profile.reference_name} />
                <Field label="Teléfono" value={profile.reference_phone} />
                <Field label="Relación" value={profile.reference_relation} />
              </div>
            </Section>
          )}

          {/* Client jobs */}
          {!isWorker && user.jobs && user.jobs.length > 0 && (
            <Section title="Trabajos publicados" icon={<Briefcase className="w-5 h-5" />}>
              <div className="space-y-2">
                {user.jobs.slice(0, 10).map((j) => (
                  <div key={j.id} className="text-sm text-onSurface-var">
                    • {j.title}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {stats && (
            <Section title="Actividad" icon={<History className="w-5 h-5" />}>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(stats)
                  .filter(([, v]) => typeof v === 'number')
                  .map(([key, value]) => (
                    <div key={key} className="bg-surface-dim rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-onSurface">
                        {key.includes('rating') ? Number(value).toFixed(1) : value}
                      </p>
                      <p className="text-[11px] text-onSurface-mute leading-tight">{statLabels[key] || key}</p>
                    </div>
                  ))}
              </div>
            </Section>
          )}

          {isPendingWorker && (
            <Section title="Acciones de moderación" icon={<ShieldCheck className="w-5 h-5" />}>
              <div className="space-y-2">
                <button
                  onClick={() => handleVerify('approved')}
                  disabled={acting}
                  className="w-full px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  Aprobar profesional
                </button>
                <button
                  onClick={handleRequestCorrection}
                  disabled={acting}
                  className="w-full px-4 py-2.5 rounded-xl border border-outlineVarWarm text-onSurface-var text-sm font-medium hover:bg-surface-var disabled:opacity-50 transition-colors"
                >
                  Solicitar correcciones
                </button>
                <button
                  onClick={() => handleVerify('rejected')}
                  disabled={acting}
                  className="w-full px-4 py-2.5 rounded-xl border border-danger-200 text-danger-600 text-sm font-medium hover:bg-danger-50 disabled:opacity-50 transition-colors"
                >
                  Rechazar perfil
                </button>
              </div>
            </Section>
          )}

          <Section title="Historial de moderación" icon={<History className="w-5 h-5" />}>
            {activity.length === 0 ? (
              <p className="text-sm text-onSurface-mute">Sin actividad registrada todavía.</p>
            ) : (
              <div className="space-y-3">
                {activity.map((a) => (
                  <div key={a.id} className="text-sm border-b border-outlineVarWarm last:border-0 pb-3 last:pb-0">
                    <p className="font-medium text-onSurface">{activityLabels[a.action] || a.action}</p>
                    <p className="text-xs text-onSurface-mute">
                      {a.user ? `${a.user.first_name} ${a.user.last_name}` : 'Sistema'} · {formatDateTime(a.created_at)}
                    </p>
                    {a.metadata?.moderation_notes && (
                      <p className="text-xs text-onSurface-var mt-1">"{a.metadata.moderation_notes}"</p>
                    )}
                    {a.metadata?.rejection_reason && (
                      <p className="text-xs text-onSurface-var mt-1">"{a.metadata.rejection_reason}"</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div>
    <p className="text-onSurface-mute text-xs">{label}</p>
    <p className="text-onSurface">{value || '—'}</p>
  </div>
);
